"use client";

import { useEffect } from "react";
import AppErrorFallback from "@/components/AppErrorFallback";
import { recoverFromChunkError } from "@/components/ChunkErrorRecovery";
import { reportClientError } from "@/lib/clientErrorDiagnostics";
import { isDeploymentRecoveryEnabled } from "@/lib/deploymentRecoveryEnvironment";
import { isChunkLoadError } from "@/lib/chunkErrorRecovery";
import { collectErrorBoundaryContext } from "@/lib/errorBoundaryContext";

function shouldLogChunkDiagnostics() {
  if (process.env.NEXT_PUBLIC_CHUNK_RECOVERY_DEBUG === "1") return true;
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage?.getItem("CHUNK_RECOVERY_DEBUG") === "1";
  } catch {
    return false;
  }
}

export default function GlobalError({ error, reset }) {
  const isChunkError = isChunkLoadError(error);
  const ownsChunkRecovery = isChunkError && isDeploymentRecoveryEnabled();

  useEffect(() => {
    if (ownsChunkRecovery) {
      void recoverFromChunkError(error, { source: "app-error" });
      return;
    }
    const pathname =
      typeof window !== "undefined" ? window.location.pathname : "";
    // Context only: which surface, which worker state, which navigation. It
    // is what was missing when an installed-PWA error screen was reported and
    // nothing in the logs could say whether it was a chunk, a navigation or an
    // expired session. Every value is a boolean or a fixed literal.
    reportClientError({
      error,
      source: "app-error",
      pathname,
      extra: { context: collectErrorBoundaryContext() },
    });
    if (shouldLogChunkDiagnostics()) {
      console.error("[PUBLIC_ERROR]", {
        message: error?.message,
        stack: error?.stack,
        pathname,
      });
    }
  }, [error, ownsChunkRecovery]);

  if (ownsChunkRecovery) {
    return null;
  }

  return <AppErrorFallback reset={reset} error={error} />;
}
